import { useState, useCallback } from 'react';
import { Product } from '../types';
import { productService, handleApiError, ProductResponse, ProductWithPricing } from '../services';
import { API_BASE_URL } from '../config/constants';

/** Resolve image_url: convert relative /uploads/... paths to absolute URLs */
const resolveImageUrl = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) {
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    return `${baseUrl}${url}`;
  }
  return url;
};

export interface ProductFilters {
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
  family_code?: string;
  subfamily_code?: string;
  brand_code?: string;
  category_item_code?: string;
}

export interface UseProductsReturn {
  products: Product[];
  categories: string[];
  isLoading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  loadProducts: (filters?: ProductFilters) => Promise<void>;
  loadMoreProducts: () => Promise<void>;
  searchProducts: (query: string) => Promise<void>;
  loadCategories: () => Promise<void>;
  clearError: () => void;
}

export const useProducts = (): UseProductsReturn => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<ProductFilters>({});

  // Helper function to map API Product to frontend Product (catalog with pricing)
  const mapCatalogProductToFrontend = (apiProduct: ProductWithPricing): Product => ({
    id: apiProduct.id,
    partNum: apiProduct.epicor_part_number,
    epicor_part_number: apiProduct.epicor_part_number,
    partDescription: apiProduct.description,
    name: apiProduct.name,
    description: apiProduct.description,
    categoryId: apiProduct.category,
    category: apiProduct.category,
    subcategory: apiProduct.subcategory,
    family_code: apiProduct.family_code,
    family_name: apiProduct.family_name,
    subfamily_code: apiProduct.subfamily_code,
    subfamily_name: apiProduct.subfamily_name,
    brand_code: apiProduct.brand_code,
    brand_name: apiProduct.brand_name,
    category_item_code: apiProduct.category_item_code,
    category_item_name: apiProduct.category_item_name,
    product_code: apiProduct.product_code,
    unitPrice: apiProduct.discounted_price || apiProduct.unit_price || undefined, // Use discounted price first
    availableQty: undefined, // Not needed for B2B portal
    unitOfMeasure: apiProduct.unit_of_measure,
    unit_of_measure: apiProduct.unit_of_measure,
    msdsUrl: apiProduct.msds_url || undefined,
    imageUrl: resolveImageUrl(apiProduct.image_url),
    image_url: resolveImageUrl(apiProduct.image_url),
    netVolume: undefined,
    netVolumeUOM: undefined,
    is_active: apiProduct.is_active,
    created_at: apiProduct.created_at,
    last_sync_at: apiProduct.last_sync_at,
    // Include pricing fields
    unit_price: apiProduct.unit_price,
    discount_percent: apiProduct.discount_percent,
    discounted_price: apiProduct.discounted_price,
    promotion_discount: apiProduct.promotion_discount,
    price_effective_from: apiProduct.price_effective_from,
    price_effective_to: apiProduct.price_effective_to,
  });


  const loadProducts = useCallback(async (filters: ProductFilters = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Always use customer catalog endpoint for authenticated customers
      const response = await productService.getCustomerCatalog({
        limit: filters.limit || 20,
        offset: filters.offset || 0,
        category: filters.category,
        search: filters.search,
        family_code: filters.family_code,
        subfamily_code: filters.subfamily_code,
        brand_code: filters.brand_code,
        category_item_code: filters.category_item_code,
      });

      const mappedProducts = response.products.map(mapCatalogProductToFrontend);
      
      if (filters.offset === 0) {
        // New search/filter - replace products
        setProducts(mappedProducts);
      } else {
        // Load more - append products
        setProducts(prev => [...prev, ...mappedProducts]);
      }
      
      setTotal(response.total_count);
      setCurrentOffset(filters.offset || 0);
      setCurrentFilters(filters);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMoreProducts = useCallback(async () => {
    if (isLoading) return;
    
    const nextOffset = currentOffset + (currentFilters.limit || 20);
    await loadProducts({
      ...currentFilters,
      offset: nextOffset,
    });
  }, [isLoading, currentOffset, currentFilters, loadProducts]);

  const searchProducts = useCallback(async (query: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use customer catalog with search parameter
      const response = await productService.getCustomerCatalog({ 
        search: query, 
        limit: 20,
        offset: 0 
      });
      const mappedProducts = response.products.map(mapCatalogProductToFrontend);
      
      setProducts(mappedProducts);
      setTotal(response.total_count);
      setCurrentOffset(0);
      setCurrentFilters({ search: query, limit: 20 });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await productService.getProductCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load categories:', err);
      // Don't set error for categories as it's not critical
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const hasMore = products.length < total;

  return {
    products,
    categories,
    isLoading,
    error,
    total,
    hasMore,
    loadProducts,
    loadMoreProducts,
    searchProducts,
    loadCategories,
    clearError,
  };
};